import { Field as ChakraField, Input, Text, VStack } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useDeleteAllContactsByPlatformMutation } from '@services/chat/chat.api';
import { useDeletePlatformMutation } from '@services/config/platform.api';
import Yup from '@utils/validation/yup-extended';
import { Field, Formik } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Platform } from '@/types/config/config.types';

type Props = {
  selectedPlatform: Platform | null;
  setSelectedPlatform: Dispatch<SetStateAction<Platform | null>>;
};

export default function DeletePlatformModal({ selectedPlatform, setSelectedPlatform }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [deletePlatform, { isLoading }] = useDeletePlatformMutation();
  const [deleteAllContactsByPlatform, { isLoading: isLoadingDeleteContacts }] =
    useDeleteAllContactsByPlatformMutation();

  return (
    <Formik
      initialValues={{
        platformName: '',
      }}
      onSubmit={() => {
        if (!selectedPlatform) {
          return;
        }
        deletePlatform(selectedPlatform.id)
          .unwrap()
          .then(() => {
            createToast(
              formatMessage({ id: 'components.platforms.message.delete' }, { name: selectedPlatform.name }),
              ToastStatusEnum.SUCCESS,
            );
            setSelectedPlatform(null);
          });
        deleteAllContactsByPlatform({ site: selectedPlatform.site, platformCode: selectedPlatform.code })
          .unwrap()
          .catch(() => {
            createToast(formatMessage({ id: 'components.platforms.message.contactFailure' }), ToastStatusEnum.WARNING);
          });
      }}
      validationSchema={
        selectedPlatform &&
        Yup.object({
          platformName: Yup.string().required().oneOf([selectedPlatform.name.toUpperCase()]),
        })
      }
      validateOnMount
    >
      {({ errors, touched, isValid, handleSubmit }) => {
        const confirmationName = selectedPlatform?.name.toUpperCase();
        return (
          <CockpitDialog
            open={selectedPlatform !== null}
            isLoading={isLoading || isLoadingDeleteContacts}
            isDisabled={!isValid}
            onCancel={() => setSelectedPlatform(null)}
            onConfirm={handleSubmit}
            otherChildren={
              <VStack width="100%" gap={2} padding={3} border="2px solid" borderColor="neutral.800">
                <Text color="neutral.300" width="100%">
                  <FormattedMessage
                    id="components.platforms.popup.descriptionDeleteConfirmation"
                    values={{ name: confirmationName }}
                  />
                </Text>
                <ChakraField.Root variant="cockpit" required invalid={touched.platformName && !!errors.platformName}>
                  <Field as={Input} placeholder={confirmationName} type="text" name="platformName" id="platformName" />
                </ChakraField.Root>
              </VStack>
            }
          >
            <Text color="neutral.100" width="100%" whiteSpace="pre-line">
              <FormattedMessage
                id="components.platforms.popup.descriptionDelete"
                values={{ name: selectedPlatform?.name }}
              />
            </Text>
          </CockpitDialog>
        );
      }}
    </Formik>
  );
}
